import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import type { SelectProps } from 'components/common';
import { CommonSelect, PlaceholderNoAccess } from 'components/common';
import { type GetAccountsParams, useInfiniteAccounts } from '../api';
import type { Account } from '../types';
import { useInView } from 'react-intersection-observer';
import { usePermissionGuard } from 'features/login';

interface SelectAccountsAttrs {
  open: boolean;
  multiples: boolean;
  placeholder: string;
  params?: GetAccountsParams;
  asAutocomplete?: boolean;
}

type SelectAccountsProps = Partial<
  Pick<
    SelectProps,
    'handleChange' | 'name' | 'inputLabel' | 'onClose' | 'disabled'
  > &
    SelectAccountsAttrs
>;

const SelectAccountsComponent: FC<SelectAccountsProps> = ({
  handleChange,
  name,
  inputLabel,
  multiples,
  placeholder,
  onClose,
  open,
  asAutocomplete,
  params,
  disabled,
}) => {
  const { ref, inView } = useInView({
    threshold: 0,
  });
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteAccounts({ params });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const options = useMemo(() => {
    if (!data) return [];
    return data.pages.flatMap((page) =>
      page.data.map(({ name, ad_account_id }: Account) => ({
        label: name,
        value: ad_account_id,
      }))
    );
  }, [data]);

  return (
    <CommonSelect
      name={name ?? 'accounts'}
      inputLabel={inputLabel ?? 'Accounts'}
      options={options}
      handleChange={handleChange}
      placeholder={placeholder}
      multiples={multiples}
      onClose={onClose}
      open={open}
      loading={isFetchingNextPage}
      loadMoreRef={ref}
      asAutocomplete={asAutocomplete}
      disabled={disabled}
    />
  );
};

const SelectAccounts: FC<SelectAccountsProps> = (props) => {
  const { accessToViewFacebookadaccount } = usePermissionGuard();
  if (!accessToViewFacebookadaccount)
    return <PlaceholderNoAccess title={'No access to view account'} />;

  return <SelectAccountsComponent {...props} />;
};

export default SelectAccounts;
